import { FormSchema } from '../../../types/formSchema';

export const rvmFormSchema: FormSchema = {
  id: 'rvm-create',
  title: 'Create RVM Analysis',
  description: 'Rate-Volume-Mix variance analysis configuration',
  sections: [
    {
      title: 'Analysis Details',
      description: 'Provide your analysis\'s basic details',
      layout: 'single-column',
      showDividerAfter: true,
      fields: [
        {
          type: 'text',
          name: 'analysisName',
          label: 'Analysis Name',
          placeholder: 'Enter RVM Analysis Name',
          validation: [
            { required: true, message: 'Analysis Name is required' }
          ],
          componentProps: {
            allowClear: true,
            showCount: true,
            maxLength: 100
          }
        },
        {
          type: 'textarea',
          name: 'analysisDescription',
          label: 'Analysis Description',
          placeholder: 'Briefly describe what this analysis is for and the business questions it will answer',
          componentProps: {
            allowClear: true,
            showCount: true,
            maxLength: 500,
            autoSize: { minRows: 3, maxRows: 6 }
          }
        }
      ]
    },
    {
      title: 'Data Source',
      description: 'Provide Data source for analysis',
      layout: 'single-column',
      showDividerAfter: true,
      fields: [
        {
          type: 'upload',
          name: 'dataSource',
          label: 'Upload Data File',
          showFieldsInfo: true,
          requiredFields: [
            'Scenario (e.g., Actual, Budget, Forecast)',
            'Period (e.g., 2024-Q1, 2024-Q2)',
            'At least one dimension (e.g., FC Type, Size)',
            'One volume metric (e.g., Units, Volume)',
            'One metric (e.g., Cost, Revenue)'
          ],
          optionalFields: [
            'Additional metrics for analysis',
            'Pre-calculated rate metrics',
            'Additional dimensions for grouping'
          ],
          uploadConfig: {
            accept: ['.csv'],
            supportedFormats: ['CSV'],
            maxSize: 1024 * 1024 * 1024, // 1GB
            maxSizeDisplay: '1 GB',
            multiple: false,
            dragDropText: 'Choose files or drag and drop'
          },
          validation: [
            { required: true, message: 'Data file is required' }
          ]
        }
      ]
    },
    {
      title: 'Analysis Parameters',
      description: 'Drag and drop columns from left to assign them to analysis parameters, or create custom rate metrics',
      layout: 'single-column',
      // Show only after CSV file is uploaded (demo mode - no API dependency)
      conditional: {
        operator: 'AND',
        conditions: [
          {
            field: 'dataSource',
            operator: 'exists'
          }
        ]
      },
      fields: [
        {
          type: 'columnMapper',
          name: 'columnMapping',
          label: 'Column Assignment',
          tooltip: 'Drag columns from the available list to assign them to analysis categories. You can create custom rate metrics by combining base metrics.',
          validation: [
            { required: true, message: 'Column mapping is required' }
          ],
          // Fixed: Move zones directly to field level to match component expectations
          zones: [
            {
              id: 'baseMetrics',
              label: 'Base Metrics',
              placeholder: 'Core business metrics (Cost, Revenue, etc.)',
              multiple: true, // Allow multiple items - per RVM requirements
              accepts: ['INTEGER', 'DECIMAL']
            },
            {
              id: 'volumeMetrics', 
              label: 'Volume Metrics',
              placeholder: 'Quantity-based metrics (Units, Volume, etc.)',
              multiple: true, // Allow multiple items - per RVM requirements
              accepts: ['INTEGER', 'DECIMAL']
            },
            {
              id: 'rateMetrics',
              label: 'Rate Metrics', 
              placeholder: 'Per-unit calculations (Cost per Unit, etc.)',
              multiple: true, // Allow multiple items - per RVM requirements
              accepts: ['INTEGER', 'DECIMAL']
            },
            {
              id: 'mixDimensions',
              label: 'Mix Dimensions',
              placeholder: 'Categorical fields for breakdown analysis',
              multiple: true, // Allow multiple items - per RVM requirements
              accepts: ['STRING']
            },
            {
              id: 'scenario',
              label: 'Scenario',
              placeholder: 'Scenario identifier (Actual, Budget, etc.)',
              multiple: false, // Single item only - per RVM requirements
              required: true,
              accepts: ['STRING']
            },
            {
              id: 'period',
              label: 'Period',
              placeholder: 'Time period identifier',
              multiple: false, // Single item only - per RVM requirements
              required: true,
              accepts: ['STRING']
            }
          ],
          // Enable custom metric creation
          customMetricCreation: true
        }
      ]
    }
  ]
};
